<!-- eslint-disable max-len -->
<template>
  <q-page padding class="finance-report">
    <q-btn v-if="diagnosticsAvailable" flat color="primary" :label="$t('reportParity.diagnostics')" :to="$route.path.replace(/billing-report$/, 'billing-diagnostics')" class="float-right" />
    <h1>{{ $t('reportCatalog.builtins.billing-report.title') }}</h1>
    <p class="text-grey-7">{{ comparison ? t('statementScope') : mode === 'billed-units' ? t('note') : t('usageNote') }}</p>
    <q-card flat bordered class="q-mb-lg">
      <q-tabs v-model="mode" active-color="primary" align="left" @update:model-value="switchReport">
        <q-tab name="billed-units" :label="t('units')" no-caps :disable="loading" />
        <q-tab v-if="statementAvailable" name="cdek-statement" :label="t('statement')" no-caps :disable="loading" />
        <q-tab name="billed-usage" :label="t('usage')" no-caps :disable="loading" />
      </q-tabs>
      <q-form @submit="refresh"><q-card-section class="row q-col-gutter-md items-end">
        <q-input v-model="filters.from" outlined hide-bottom-space type="date" class="col-12 col-sm-3" :label="t('from')" :disable="loading" :rules="[v => !!v]" />
        <q-input v-model="filters.to" outlined hide-bottom-space type="date" class="col-12 col-sm-3" :label="t('to')" :disable="loading" :rules="[v => !!v && v >= filters.from]" />
        <q-input v-if="!comparison" v-model="filters.account" outlined type="number" min="1" class="col-12 col-sm-3" :label="t('account')" :disable="loading" />
        <div class="col-12 col-sm-3"><q-btn type="submit" color="primary" icon="refresh" :label="t('refresh')" :loading="loading" /></div>
      </q-card-section></q-form>
      <q-card-section class="q-pt-none row items-center q-gutter-sm">
        <q-btn flat no-caps :label="t('yearToDate')" :disable="loading" @click="preset('year')" />
        <q-btn flat no-caps :label="t('thisMonth')" :disable="loading" @click="preset('month')" />
        <span class="text-grey-7">{{ t('rangeLimit') }}</span>
      </q-card-section>
    </q-card>
    <q-banner v-if="error" class="bg-red-1 text-negative q-mb-md" role="alert">{{ t('error') }}</q-banner>
    <p v-if="!result && !loading && !error" class="text-grey-7" role="status">{{ t('runHint') }}</p>
    <template v-if="result">
      <div class="row items-center justify-between q-mb-md text-grey-7">
        <span>{{ t('selectedPeriod') }}: {{ dateLabel(applied.from) }} — {{ dateLabel(applied.to) }}</span>
        <span>{{ comparison ? t('statementScope') : `${t('account')}: ${applied.account || t('accessibleAccounts')}` }}</span>
      </div>
      <div v-if="!comparison" class="row q-col-gutter-md q-mb-lg">
        <div v-for="key in metrics" :key="key" class="col-12 col-sm-6 col-lg-3"><q-card flat bordered class="full-height"><q-card-section>
          <div class="text-grey-7">{{ metricLabel(key) }}</div><strong class="text-h5">{{ number(totals[key]) }}</strong>
        </q-card-section></q-card></div>
      </div>
      <q-card flat bordered class="q-mb-lg">
        <q-card-section class="row items-center justify-between chart-heading">
          <h2>{{ t('activity') }}</h2>
          <q-select v-if="!comparison" v-model="chartMetric" outlined dense emit-value map-options :options="chartOptions" :label="t('chartMetric')" class="chart-selector" />
        </q-card-section>
        <q-tabs v-if="!comparison" v-model="grouping" align="left" active-color="primary">
          <q-tab name="month" :label="t('monthly')" no-caps />
          <q-tab name="day" :label="t('daily')" no-caps />
        </q-tabs>
        <q-separator />
        <q-card-section>
          <div class="chart-legend q-mb-md"><span v-for="(key, index) in seriesKeys" :key="key"><i :class="`series-${index}`"></i>{{ metricLabel(key) }}</span></div>
          <p v-if="!rows.length" class="text-grey-7">{{ t('empty') }}</p>
          <div v-else class="chart-scroll">
            <svg class="billing-chart" viewBox="0 0 1000 300" role="img" :aria-label="t('activity')">
              <g v-for="tick in [0, 1, 2, 3, 4]" :key="tick">
                <line x1="70" x2="980" :y1="20 + tick * 60" :y2="20 + tick * 60" class="chart-grid" />
                <text x="60" :y="24 + tick * 60" text-anchor="end">{{ number(Math.round(chartMax * (1 - tick / 4))) }}</text>
              </g>
              <template v-for="(row, index) in rows" :key="row.date">
                <text v-if="index % Math.max(1, Math.ceil(rows.length / 12)) === 0 || index === rows.length - 1" :x="chartX(index)" y="285" text-anchor="middle">{{ chartDate(row.date) }}</text>
                <template v-for="(key, series) in seriesKeys" :key="key">
                  <rect v-if="row[key] != null" :x="chartX(index) - (seriesKeys.length * barWidth / 2) + series * barWidth" :y="260 - (row[key] / chartMax * 240)" :width="Math.max(1, barWidth - 2)" :height="Math.max(0, row[key] / chartMax * 240)" :class="`series-${series}`">
                    <title>{{ dateLabel(row.date) }} · {{ metricLabel(key) }}: {{ number(row[key]) }}</title>
                  </rect>
                </template>
              </template>
            </svg>
          </div>
          <p class="text-grey-7 q-mb-none">{{ mode === 'billed-usage' ? t('usageNote') : t('cohortNote') }}</p>
          <p class="text-grey-7 q-mb-none">{{ t('partialNote') }}</p>
          <p v-if="comparison" class="text-grey-7 q-mb-none">{{ t('statementSource') }}</p>
        </q-card-section>
      </q-card>
      <q-table flat bordered :class="{ 'statement-table': comparison }" :title="t(grouping === 'month' ? 'monthly' : 'daily')" :rows="rows" :columns="columns" row-key="date" :pagination="{ rowsPerPage: grouping === 'month' ? 12 : 31 }"
        :no-data-label="t('empty')" :rows-per-page-label="$t('Rows per page')" />
      <q-expansion-item :label="t('definitions')" class="q-mt-md" dense>
        <div class="q-pa-md text-grey-7">{{ t('repeatNote') }}<br>{{ t('comparisonNote') }}</div>
      </q-expansion-item>
    </template>
  </q-page>
</template>
<script>
/* eslint-disable max-len */
import { HALApiService } from '../../services/hal-api.service.js';

const localDate = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
const unitFields = ['billedUnits', 'billedOrders', 'createdDateBilledUnits', 'createdDateBilledOrders', 'rawBilledUnits', 'repeatedTransactionUnits', 'billingRows'];
const usageFields = ['billedOrders', 'billedStorageItems'];
// Aggregate only the API's scoped daily facts. An absent value remains unavailable.
const sum = (rows, key) => (rows.length && rows.every(row => row[key] != null && Number.isFinite(Number(row[key]))) ? rows.reduce((total, row) => total + Number(row[key]), 0) : null);
export default {
  name: 'BillingReport',
  data() {
    const now = new Date();
    return {
      diagnosticsAvailable: false, statementAvailable: false, mode: 'billed-units', grouping: 'month', chartMetric: 'units',
      filters: { from: localDate(new Date(now.getFullYear(), 0, 1)), to: localDate(now), account: '' },
      applied: {}, loading: false, error: false, result: null, requestId: 0,
    };
  },
  computed: {
    comparison() { return this.mode === 'cdek-statement'; },
    fields() { if (this.comparison) return ['statementUnits', 'billedUnits', 'billedDifference', 'createdDateBilledUnits', 'createdDifference', 'status']; return this.mode === 'billed-units' ? unitFields : usageFields; },
    metrics() { return this.mode === 'billed-units' ? [...unitFields, 'createdOutsidePeriodUnits'] : usageFields; },
    daily() { return this.mode === 'billed-units' ? this.result?.daily || [] : this.result || []; },
    totals() { return this.mode === 'billed-units' ? this.result?.total || {} : Object.fromEntries(this.fields.map(key => [key, sum(this.daily, key)])); },
    monthly() {
      if (this.comparison) return this.result?.monthly || [];
      const months = {};
      this.daily.forEach(row => { const month = row.date.slice(0, 7); (months[month] ||= []).push(row); });
      return Object.keys(months).sort().map((month) => {
        const monthEnd = localDate(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0));
        const complete = this.applied.from <= `${month}-01` && this.applied.to >= monthEnd && monthEnd < localDate(new Date());
        return { date: month, complete, ...Object.fromEntries(this.fields.map(key => [key, sum(months[month], key)])) };
      });
    },
    rows() {
      if (this.comparison) return this.monthly;
      const source = this.grouping === 'month' ? this.monthly : this.daily;
      return source.map((row, index) => {
        const previous = source[index - 1];
        const key = this.seriesKeys[0];
        const comparable = this.grouping === 'month' && row.complete && previous?.complete;
        const change = comparable && row[key] != null && previous[key] > 0 ? ((row[key] - previous[key]) / previous[key]) * 100 : null;
        const difference = row.billedUnits != null && row.createdDateBilledUnits != null ? row.billedUnits - row.createdDateBilledUnits : null;
        return { ...row, difference, change };
      });
    },
    chartOptions() { return (this.mode === 'billed-units' ? ['units', 'orders'] : ['orders', 'storage']).map(value => ({ value, label: this.t(value) })); },
    seriesKeys() {
      if (this.comparison) return ['statementUnits', 'billedUnits', 'createdDateBilledUnits'];
      if (this.mode === 'billed-usage') return [this.chartMetric === 'storage' ? 'billedStorageItems' : 'billedOrders'];
      return this.chartMetric === 'orders' ? ['billedOrders', 'createdDateBilledOrders'] : ['billedUnits', 'createdDateBilledUnits'];
    },
    chartMax() { return Math.max(4, ...this.rows.flatMap(row => this.seriesKeys.map(key => Number(row[key]) || 0))); },
    barWidth() { return Math.min(30, 910 / Math.max(1, this.rows.length) / (this.seriesKeys.length + 1)); },
    columns() {
      const fields = ['date', ...this.fields, ...(this.mode === 'billed-units' ? ['difference'] : []), ...(!this.comparison && this.grouping === 'month' ? ['change'] : [])];
      return fields.map(key => ({ name: key, field: key, label: key === 'date' ? this.t(this.grouping === 'month' ? 'month' : 'date') : this.columnLabel(key), align: key === 'date' ? 'left' : 'right', sortable: true,
        format: key === 'status' ? value => this.t(value) : key === 'date' ? this.dateLabel : key === 'change' ? this.percent : this.number }));
    },
  },
  async created() {
    HALApiService.get('/reports/statistics/accounts/billing-diagnostics/options', {}).then((data) => { this.diagnosticsAvailable = data.available === true; }).catch(() => {});
    try { const options = await HALApiService.get('/reports/statistics/accounts/cdek-statement/options'); this.statementAvailable = options?.available === true; } catch { this.statementAvailable = false; }
  },
  beforeUnmount() { this.requestId += 1; },
  methods: {
    t(key) { return this.$t(`financeReports.${key}`); },
    columnLabel(key) { return key === 'change' ? `${this.t('change')} · ${this.metricLabel(this.seriesKeys[0])}` : this.metricLabel(key); },
    metricLabel(key) { return this.t(this.mode === 'billed-usage' && key === 'billedOrders' ? 'orderDays' : key); },
    number(value) { return value == null ? this.t('unavailable') : Number(value).toLocaleString(this.$i18n.locale); },
    percent(value) { return value == null ? '—' : `${value > 0 ? '+' : ''}${Number(value).toLocaleString(this.$i18n.locale, { maximumFractionDigits: 1 })}%`; },
    dateLabel(value) { return value ? new Date(`${value.length === 7 ? `${value}-01` : value}T12:00:00`).toLocaleDateString(this.$i18n.locale, value.length === 7 ? { month: 'long', year: 'numeric' } : {}) : ''; },
    chartDate(value) { return new Date(`${value.length === 7 ? `${value}-01` : value}T12:00:00`).toLocaleDateString(this.$i18n.locale, value.length === 7 ? { month: 'short' } : { day: 'numeric', month: 'short' }); },
    chartX(index) { return 70 + (910 * (index + 0.5)) / Math.max(1, this.rows.length); },
    clear() { this.requestId += 1; this.result = null; this.error = false; this.loading = false; },
    switchReport() { this.clear(); if (this.comparison) this.grouping = 'month'; this.chartMetric = this.mode === 'billed-units' ? 'units' : 'orders'; this.refresh(); },
    preset(period) { const now = new Date(); this.filters.from = localDate(new Date(now.getFullYear(), period === 'year' ? 0 : now.getMonth(), 1)); this.filters.to = localDate(now); this.refresh(); },
    async refresh() {
      if ((this.comparison && !this.statementAvailable) || this.loading || !this.filters.from || !this.filters.to || this.filters.from > this.filters.to) return;
      if ((Date.parse(this.filters.to) - Date.parse(this.filters.from)) / 86400000 >= 366) { this.error = true; this.result = null; return; }
      this.requestId += 1;
      const id = this.requestId; this.loading = true; this.error = false; this.result = null;
      const filter = { from: this.filters.from, to: this.filters.to };
      if (!this.comparison && this.filters.account) filter.account = this.filters.account;
      try {
        const result = await HALApiService.get(`/reports/statistics/accounts/${this.mode}/date`, { filter });
        if (id !== this.requestId) return;
        if (this.comparison) {
          if (!result || result.scope !== 'cdek' || !Array.isArray(result.monthly)) throw new Error('Unexpected comparison response');
          this.result = result;
        } else if (Array.isArray(result) && !result.length) this.result = this.mode === 'billed-units' ? { total: {}, daily: [] } : [];
        else if (this.mode === 'billed-units' ? result && result.total && Array.isArray(result.daily) : Array.isArray(result)) this.result = result;
        else throw new Error('Unexpected report response');
        this.applied = { ...filter };
      } catch { if (id === this.requestId) this.error = true; }
      finally { if (id === this.requestId) this.loading = false; }
    },
  },
};
</script>
<style scoped>
.finance-report { max-width: 1600px; margin: auto; }
h1 { font-size: 24px; line-height: 1.4; font-weight: 500; margin: 8px 0 12px; }
h2 { font-size: 18px; line-height: 1.4; font-weight: 500; margin: 0; }
strong { display: block; margin-top: 12px; }
.chart-heading { gap: 16px; }
.chart-selector { min-width: 220px; }
.chart-scroll { overflow-x: auto; }
.billing-chart { width: 100%; min-width: 650px; display: block; }
.billing-chart text { fill: #666; font-size: 12px; }
.chart-grid { stroke: #e0e0e0; stroke-width: 1; }
.chart-legend { display: flex; flex-wrap: wrap; gap: 20px; }
.chart-legend span { display: inline-flex; align-items: center; gap: 8px; }
.chart-legend i { width: 14px; height: 14px; }
.series-0 { fill: var(--q-primary); background: var(--q-primary); }
.series-2 { fill: #bc842c; background: #bc842c; }
.series-1 { fill: #7b8794; background: #7b8794; }
.statement-table :deep(table) { table-layout: fixed; min-width: 820px; }
.statement-table :deep(th), .statement-table :deep(td) { white-space: normal; overflow-wrap: anywhere; }
.statement-table :deep(th) { line-height: 1.5; padding-top: 12px; padding-bottom: 12px; }
.finance-report :deep(.q-card), .finance-report :deep(.q-table__container),
.finance-report :deep(.q-field__control), .finance-report :deep(.q-btn),
.finance-report :deep(.q-banner) { border-radius: 0; }
</style>
